import { z } from 'zod';
// B3-4 (16-9): the Checkliste carries what IAZI asks — the vocabularies are the
// Bewertung's own, so the prefill (B3-3) copies 1:1 instead of translating.
import { GEBAEUDETYPEN, HAUSTYPEN, WOHNUNGSTYPEN, bandIntervallSchema } from './valuation';
import { LUXUS_STUFEN, VOLUMEN_NORMEN, ZAEHLWEISEN, wohnungsspiegelSchema } from '../avm';
/**
 * MLG Verkaufs-Pipeline (F1 fundament) — shared contracts.
 *
 * The sales pipeline (Lead → Contract → Checkliste → Dokumente → Shooting →
 * Exposé → Publikation) mirrors the financing dossier structure: a `Mandate`
 * owns checklist sections / documents / shooting exactly like a
 * `FinancingRequest` owns dossier forms / uploads. TENANT-SHARED and
 * `type`-discriminated (sale now, mortgage later) — never MLG-only.
 *
 * Enums live here as the single source of truth so the CRM, the portal and the
 * backend validation all use the identical dropdown option lists.
 */
// ─────────────────────────── Mandate ────────────────────────────────────────
/** verkoop nu; hypotheek later (tenant-shared pipeline, see workflow doc §P2). */
export const MANDATE_TYPES = ['sale', 'mortgage'];
export const mandateTypeSchema = z.enum(MANDATE_TYPES);
/** The pipeline state machine (workflow doc §7). Forward-flowing. */
export const MANDATE_STATUSES = [
    'lead',
    'contract_draft',
    'contract_sent',
    'mandate_signed', // the gate: only here is it a real mandate + portal account
    'checklist_in_progress',
    'checklist_complete',
    'documents_pending',
    'documents_complete',
    'expose_ready',
    'published',
    'won',
    'lost',
];
export const mandateStatusSchema = z.enum(MANDATE_STATUSES);
/**
 * Sales-mandate statuses from which a deal may legally be CLOSED as won: every
 * status at/after `mandate_signed` (a real, signed mandate) that is not already
 * terminal (`won`/`lost`). Derived from `MANDATE_STATUSES` so the two never
 * drift. The admin Finanzen "als verkauft markieren" transition is guarded by
 * this list — a lead / contract_draft / contract_sent or an already won/lost
 * mandate can NOT be marked won (editing a commission price is status-neutral).
 */
export const WON_ELIGIBLE_STATUSES = MANDATE_STATUSES.slice(MANDATE_STATUSES.indexOf('mandate_signed')).filter((status) => status !== 'won' && status !== 'lost');
/** Whether a mandate in this status may be explicitly marked won (guarded transition). */
export function isWonEligibleStatus(status) {
    return WON_ELIGIBLE_STATUSES.includes(status);
}
/**
 * The transitions of a sales mandate's life, as AUDIT ACTIONS (`audit_logs.action`).
 *
 * ONE SOURCE FOR TWO READERS (docs/ANALYTICS-INTERN-EXTERN-ONTWERP.md §3.1, «geen
 * tweede cijfer-definitie»; master 27-9 14:40):
 *  - the Leitstand KPI `mandate.phasen_uebergaenge`
 *    (`apps/backend/src/control-analytics/kpi-register.ts`, which also says why the
 *    trail and not the status column, and what the trail cannot see);
 *  - the Makler card «Phasenwechsel im Zeitraum» on the CRM Statistik (PR B2a,
 *    `apps/backend/src/mandate/mandate-analytics.service.ts`).
 * It lives HERE because neither reader may import the other: a product may not
 * import the control plane and the control plane may not import a product
 * (`no-product-to-control` / `no-control-to-product`, `.dependency-cruiser.cjs`).
 *
 * Moved VERBATIM out of `kpi-register.ts` in PR B2a — the same seven values in the
 * same order, so the KPI keeps its meaning and `KPI_REGISTER_VERSION` does not bump.
 */
export const MANDAT_UEBERGAENGE = [
    'contract.sent',
    'contract.signed',
    'contract.marked_signed_manually',
    'mandate.marked_won',
    'mandate.marked_lost',
    'mandate.lost_reopened',
    'mandate.reopened',
];
/** 0-based rank of a status in the forward-flowing pipeline (−1 for unknown). */
export function mandateStatusRank(status) {
    return MANDATE_STATUSES.indexOf(status);
}
const MANDATE_SIGNED_RANK = MANDATE_STATUSES.indexOf('mandate_signed');
/**
 * The sign-gate: true iff the mandate is at/after `mandate_signed` (a real,
 * signed mandate) and not terminal (`won`/`lost`). Same predicate as
 * `WON_ELIGIBLE_STATUSES`, derived from the `MANDATE_STATUSES` order so the two
 * never drift.
 *
 * Owner decision 2026-08-07: this gates exactly TWO actions —
 *   1. channel PUBLICATION (a listing must never go live unsigned), and
 *   2. scheduling a Foto-SHOOTING (booking a crew + the owner's calendar).
 * Everything else — Exposé generate/regenerate, PDF, photos, checklist,
 * documents, portal — deliberately works on an UNSIGNED mandate so staff can
 * prepare the whole listing while the Maklervertrag is still out for signature.
 */
export function isSignedMandateStatus(status) {
    return mandateStatusRank(status) >= MANDATE_SIGNED_RANK && status !== 'won' && status !== 'lost';
}
/**
 * Statuses from which a successful Exposé generate may ADVANCE the mandate to
 * `expose_ready`: every signed, non-terminal status that is not already
 * `published` (a live listing must never regress).
 *
 * This list is the reason the sign-gate shift is safe. `expose_ready` OUTRANKS
 * `mandate_signed`, so advancing an unsigned mandate on generate would flip
 * `isSignedMandateStatus` to true and silently defeat both remaining gates — an
 * unsigned lead could be published by first generating an Exposé. Generating on
 * an unsigned mandate therefore stores the Exposé and leaves the pipeline status
 * exactly where it was; the status only ever crosses `mandate_signed` when the
 * Maklervertrag is actually signed.
 */
export const EXPOSE_READY_ADVANCE_STATUSES = WON_ELIGIBLE_STATUSES.filter((status) => status !== 'published');
// ─────────────────────────── Checkliste sections ────────────────────────────
/** The 8 wizard steps (workflow doc §3.1). Umgebung has no manual fields — it
 *  is auto-derived at Exposé time — but stays a section for status/order. */
export const CHECKLIST_SECTIONS = [
    'mandat',
    'objekt',
    'flaechen',
    'finanzen',
    'energie',
    'zustand',
    'ausstattung',
    'umgebung',
];
export const checklistSectionSchema = z.enum(CHECKLIST_SECTIONS);
/**
 * German display labels for the 8 checklist sections. Single source shared by the
 * CRM, the customer portal (read-only checklist view) and the backend (change-
 * request notification text) so the section names never drift across surfaces.
 */
export const CHECKLIST_SECTION_LABELS = {
    mandat: 'Mandat & Eigentümer',
    objekt: 'Objekt',
    flaechen: 'Flächen',
    finanzen: 'Preise & Finanzen',
    energie: 'Energie & Heizung',
    zustand: 'Zustand & Qualität',
    ausstattung: 'Ausstattung & Umschwung',
    umgebung: 'Umgebung',
};
// ─────────────────────────── Object dropdown enums ──────────────────────────
export const IMMOBILIEN_TYPES = ['wohnung', 'efh', 'mfh', 'gewerbe', 'grundstueck'];
export const immobilienTypSchema = z.enum(IMMOBILIEN_TYPES);
export const NUTZUNG = ['wohnen', 'gewerbe', 'gemischt'];
export const nutzungSchema = z.enum(NUTZUNG);
/**
 * E19: Verkaufsobjekt-Kategorie — ein Renditeobjekt bekommt im Exposé den
 * Mieterspiegel-Block und rendite-orientierte Texte; die Kategorie steuert
 * ausserdem die "Mieterspiegel fehlt"-Warnung beim Generieren.
 */
export const OBJEKTKATEGORIE = ['selbstbewohnt', 'renditeobjekt'];
export const objektkategorieSchema = z.enum(OBJEKTKATEGORIE);
export const TRANSAKTIONSTYP = ['verkauf', 'miete'];
export const transaktionstypSchema = z.enum(TRANSAKTIONSTYP);
export const VERKAUFSVERFAHREN = ['festpreis', 'bieterverfahren', 'auf_anfrage'];
export const verkaufsverfahrenSchema = z.enum(VERKAUFSVERFAHREN);
export const VERFUEGBARKEIT = ['sofort', 'nach_vereinbarung', 'per_datum'];
export const verfuegbarkeitSchema = z.enum(VERFUEGBARKEIT);
export const ZUSTAND = ['neuwertig', 'renoviert', 'gepflegt', 'renovationsbeduerftig'];
export const zustandSchema = z.enum(ZUSTAND);
/** From the Maklervertrag (Eigentumsform) — drives STWE/Baurecht doc conditions. */
export const EIGENTUMSFORM = [
    'alleineigentum',
    'miteigentum',
    'stockwerkeigentum',
    'baurecht',
];
export const eigentumsformSchema = z.enum(EIGENTUMSFORM);
export const MWST = ['nicht_optiert', 'optiert'];
export const mwstSchema = z.enum(MWST);
export const WAERMEERZEUGUNG = [
    'waermepumpe',
    'gas',
    'oel',
    'fernwaerme',
    'pellets',
    'elektro',
    'holz',
    'solar',
    'andere',
];
export const waermeerzeugungSchema = z.enum(WAERMEERZEUGUNG);
export const WAERMEVERTEILUNG = ['bodenheizung', 'radiatoren', 'deckenheizung', 'andere'];
export const waermeverteilungSchema = z.enum(WAERMEVERTEILUNG);
export const NIEDRIGENERGIE = ['nein', 'minergie', 'minergie_p', 'minergie_a', 'andere'];
export const niedrigenergieSchema = z.enum(NIEDRIGENERGIE);
export const BAUTEIL_QUALITAET = ['einfach', 'mittel', 'gehoben', 'luxurioes'];
export const bauteilQualitaetSchema = z.enum(BAUTEIL_QUALITAET);
export const BAUTEIL_ZUSTAND = ['neuwertig', 'gut', 'mittel', 'renovationsbeduerftig'];
export const bauteilZustandSchema = z.enum(BAUTEIL_ZUSTAND);
export const JA_NEIN = ['ja', 'nein'];
export const jaNeinSchema = z.enum(JA_NEIN);
/** Bauteile assessed in the Zustand & Qualität step (2 dropdowns each). */
export const BAUTEILE = ['kueche', 'bad', 'boden', 'fenster', 'mauerwerk'];
export const bauteilSchema = z.enum(BAUTEILE);
// ─────────────────────────── Ausstattung chips ──────────────────────────────
export const AUSSTATTUNG_OPTIONS = [
    'abstellkammer',
    'auslaenderkontingent',
    'balkon',
    'bauland_erschlossen',
    'bergsicht',
    'buero_homeoffice',
    'carport',
    'cheminee',
    'doppelgarage',
    'eckhaus',
    'erdgeschoss',
    'erstwohnsitz',
    'garage',
    'garten',
    'gasanschluss',
    'geschirrspueler',
    'grillplatz',
    'hanglage',
    'haustiere_erlaubt',
    'hochparterre',
    'im_baurecht',
    'wohngemeinschaft',
    'kabelfernsehen',
    'kachelofen',
    'kamin',
    'keller',
    'kinderfreundlich',
    'ladestation_elektroauto',
    'lift',
    'mietkautionsgarantie',
    'minergie',
    'multimediale_verkabelung',
    'nichtraucher',
    'parkplatz',
    'projektiert',
    'rollstuhlgaengig',
    'ruhig',
    'sauna',
    'seesicht',
    'sommerlaube',
    'sonnig',
    'spielraum',
    'suedhang',
    'swimmingpool',
    'terrasse',
    'tumbler',
    'virtuelle_besichtigung',
    'waschmaschine',
    'waeschetrockner',
    'weinkeller',
    'zweitwohnsitz',
    'zwischennutzung',
];
export const ausstattungOptionSchema = z.enum(AUSSTATTUNG_OPTIONS);
export const ANNEHMLICHKEITEN_OPTIONS = [
    'ruhige_lage',
    'aussicht',
    'bergsicht',
    'seesicht',
    'haustiere_erlaubt',
    'kinderfreundlich',
    'seenaehe',
    'zentrale_lage',
    'oev_naehe',
    'privatsphaere',
    'sonnige_ausrichtung',
    'offenes_wohnkonzept',
    'gehobener_ausbaustandard',
];
export const annehmlichkeitOptionSchema = z.enum(ANNEHMLICHKEITEN_OPTIONS);
export const ANSCHLUESSE_OPTIONS = ['glasfaser', 'dsl', 'kabel', 'fuenf_g'];
export const anschlussOptionSchema = z.enum(ANSCHLUESSE_OPTIONS);
// ─────────────────────────── Contract / shooting / publication ──────────────
export const CONTRACT_STATUSES = ['draft', 'sent', 'signed', 'declined', 'expired'];
export const contractStatusSchema = z.enum(CONTRACT_STATUSES);
/** Customer edits land in a staged change-request first (approval gate, §6). */
export const CHANGE_REQUEST_STATUSES = ['pending', 'approved', 'rejected'];
export const changeRequestStatusSchema = z.enum(CHANGE_REQUEST_STATUSES);
export const SHOOTING_STATUSES = [
    'proposed', // advisor proposed a date
    'customer_approved',
    'customer_declined',
    'confirmed',
    'done',
    'cancelled',
];
export const shootingStatusSchema = z.enum(SHOOTING_STATUSES);
export const PUBLICATION_CHANNELS = ['casaone', 'website', 'immoscout', 'homegate'];
export const publicationChannelSchema = z.enum(PUBLICATION_CHANNELS);
// 'publishing' is the in-flight state used as an atomic CAS claim so a
// concurrent/double publish can never create a duplicate external listing (F9).
export const PUBLICATION_STATUSES = [
    'queued',
    'publishing',
    'published',
    'failed',
    'retracted',
];
export const publicationStatusSchema = z.enum(PUBLICATION_STATUSES);
// ─────────────────────────── Beilage 1 — conditional documents ──────────────
/** Document requirement groups from Beilage 1 (IMG_1561). */
export const DOC_GROUPS = ['liegenschaft', 'neubau_umbau', 'stwe', 'bauland'];
/**
 * Dokumentations-Import: an EXISTING Verkaufsdokumentation (usually a PDF from
 * ANOTHER brokerage) uploaded so the checklist fields + photos can be extracted
 * from it. Its own docKey — deliberately not `fotos` — so the import run means
 * exactly ONE document instead of a mixed folder of loose images.
 *
 * Exported as a named constant because the backend's import endpoints select on
 * it: a typo there would silently find nothing, and this is a shared contract
 * between the upload UI, the doc registry and the extract service.
 */
export const DOC_KEY_VERKAUFSDOKU = 'verkaufsdoku';
/** The verbatim Beilage-1 catalogue. "Die Liste ist nicht abschliessend" — the
 *  upload UI also allows free extra documents beyond these keys, and every doc is
 *  optional (round-2 feedback): the list is guidance, not a gate. Multiple files
 *  may be uploaded per key (no unique constraint on docKey). */
export const DOC_REQUIREMENTS = [
    // Unterlagen zur Liegenschaft (immer)
    {
        key: 'grundbuchauszug',
        label: 'Aktueller Grundbuchauszug',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'amtliche_schaetzung',
        label: 'Aktuelle amtliche Schätzung',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'externe_schaetzung',
        label: 'Externe Liegenschaftsschätzung (falls vorhanden)',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'kaufvertragsentwurf',
        label: 'Kaufvertragsentwurf (falls vorhanden)',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'katasterplan',
        label: 'Katasterplan / Situationsplan / Zonenplan',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'gebaeudeversicherung',
        label: 'Gebäudeversicherungsausweis',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'bauplaene',
        label: 'Baupläne / kubische Berechnung / Baubeschrieb',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'baurechtsvertrag',
        label: 'Baurechtsvertrag + aktueller Baurechtszins (falls im Baurecht)',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'fotos',
        label: 'Verkaufsdokumentation oder Fotos (Aussen-/Innenaufnahmen)',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: DOC_KEY_VERKAUFSDOKU,
        label: 'Bestehende Verkaufsdokumentation (PDF — für den Import)',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'energieeinstufung',
        label: 'Dokumente zur Energieeinstufung (Energienachweis, GEAK, Minergie)',
        group: 'liegenschaft',
        required: false,
    },
    {
        key: 'gebaeudeinfos',
        label: 'Zusätzliche Gebäudeinformationen (Heizungstyp, Verbrauch, Wohnfläche, Baujahr)',
        group: 'liegenschaft',
        required: false,
    },
    { key: 'mieterspiegel', label: 'Mieterspiegel', group: 'liegenschaft', required: false },
    {
        key: 'sanierungskosten',
        label: 'Kostenzusammenstellung grössere Sanierung (Abrechnung, Belege)',
        group: 'liegenschaft',
        required: false,
    },
    // Zusätzlich für Neu- und Umbauprojekte
    {
        key: 'baubewilligung',
        label: 'Baubewilligung / Rechtskraftsbescheinigung / Baufreigabe',
        group: 'neubau_umbau',
        required: false,
    },
    {
        key: 'umbauplaene',
        label: 'Bau-/Umbaupläne & Baubeschrieb / kubische Berechnung',
        group: 'neubau_umbau',
        required: false,
    },
    {
        key: 'kostenvoranschlag',
        label: 'Kostenvoranschlag mit Baukostenplan-Nr.',
        group: 'neubau_umbau',
        required: false,
    },
    {
        key: 'handwerkerverzeichnis',
        label: 'Handwerkerverzeichnis',
        group: 'neubau_umbau',
        required: false,
    },
    {
        key: 'bauversicherung',
        label: 'Bauversicherung / Bauzeitversicherung',
        group: 'neubau_umbau',
        required: false,
    },
    {
        key: 'gu_vertrag',
        label: 'Generalunternehmer- / Totalunternehmer-Vertrag',
        group: 'neubau_umbau',
        required: false,
    },
    { key: 'werkvertrag', label: 'Werkvertrag', group: 'neubau_umbau', required: false },
    {
        key: 'verkaufspreisliste',
        label: 'Verkaufspreisliste bei EFH / STWE',
        group: 'neubau_umbau',
        required: false,
    },
    // Zusätzlich bei Stockwerkeigentum
    {
        key: 'stwe_kaufpreisliste',
        label: 'Kaufpreisliste + Dokumentation',
        group: 'stwe',
        required: false,
    },
    {
        key: 'stwe_reglement',
        label: 'Nutzungs- und Verwaltungsreglement der STWE-Gemeinschaft',
        group: 'stwe',
        required: false,
    },
    {
        key: 'stwe_begruendung',
        label: 'STWE-Begründungs-Vertrag (mit Wertquotenaufteilung)',
        group: 'stwe',
        required: false,
    },
    {
        key: 'stwe_baurecht_nebenkosten',
        label: 'Bei STWE im Baurecht: Nebenkostenabrechnungen inkl. Baurechtzins',
        group: 'stwe',
        required: false,
    },
    // Zusätzlich bei Bauland
    { key: 'baureglement', label: 'Baureglement / Zonenplan', group: 'bauland', required: false },
    {
        key: 'ueberbauung',
        label: 'Überbauung / Projekte (sofern vorhanden)',
        group: 'bauland',
        required: false,
    },
    {
        key: 'altlastenkataster',
        label: 'Auszug aus dem Altlastenverdachtskataster',
        group: 'bauland',
        required: false,
    },
];
/** Which Beilage-1 groups apply for this object. `liegenschaft` is always in. */
export function applicableDocGroups(input) {
    const groups = ['liegenschaft'];
    if (input.eigentumsform === 'stockwerkeigentum')
        groups.push('stwe');
    if (input.objektTyp === 'grundstueck')
        groups.push('bauland');
    if (input.neubauUmbau)
        groups.push('neubau_umbau');
    return groups;
}
/** The document requirements that apply for a given object (conditional set). */
export function requiredDocuments(input) {
    const groups = new Set(applicableDocGroups(input));
    return DOC_REQUIREMENTS.filter((d) => groups.has(d.group));
}
// ─────────────────────────── Checkliste section schemas ─────────────────────
// Autosave stores each section as a partial JSONB draft, so every field is
// optional here; a "complete" gate can enforce required fields later. The
// shapes are the canonical field reference for the CRM + portal forms.
const num = z.number().nonnegative();
const addressSchema = z
    .object({
    strasse: z.string(),
    plz: z.string(),
    ort: z.string(),
    // `.max(2)` (not `.length(2)`): a mid-typed 1-char Kanton must not hard-fail
    // a partial autosave / create-form draft — consistent with the lenient
    // section schemas. A completeness gate can enforce exactly 2 later.
    kanton: z.string().max(2).optional(),
    land: z.string().optional(),
})
    .partial();
/**
 * B3-4 (16-9): what the Gebäuderegister (GWR, geo.admin) says about the object
 * address — captured at the address pick, shown as a HINT next to Baujahr
 * (the register knows a period, the PDF a year, the mandate another: the code
 * decides nothing, the advisor does). Additive, partial, never required.
 */
export const gwrHinweisSchema = z
    .object({
    egid: z.string(),
    bauperiode: z.number().int(),
    baujahr: z.number().int(),
    baujahrVorschlag: z.number().int(),
    baujahrGeschaetzt: z.boolean(),
    geschosse: z.number().int(),
    anzahlWohnungen: z.number().int(),
    gemeindeBfs: z.number().int(),
    e: z.number(),
    n: z.number(),
})
    .partial();
export const mandatSectionSchema = z
    .object({
    // ISO date (YYYY-MM-DD). Regex-tightened so no free-form/exotic value reaches
    // the Maklervertrag merge-data (both CRM DatePickers already emit this format).
    datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    ansprechpartnerUserId: z.string().uuid(),
    eigentuemerName: z.string(),
    eigentuemerHandy: z.string(),
    eigentuemerEmail: z.string().email(),
    objektAdresse: addressSchema,
    /** B3-4: GWR facts of the object address (hint source; see {@link gwrHinweisSchema}). */
    objektGwr: gwrHinweisSchema,
    eigentuemerAdresse: addressSchema,
    firma: z.string(),
})
    .partial();
export const objektSectionSchema = z
    .object({
    titel: z.string(),
    gemeinde: z.string(),
    immobilienTyp: immobilienTypSchema,
    nutzung: nutzungSchema,
    // E19: selbstbewohnt vs. renditeobjekt (steuert Mieterspiegel im Exposé).
    objektkategorie: objektkategorieSchema,
    transaktionstyp: transaktionstypSchema,
    verkaufsverfahren: verkaufsverfahrenSchema,
    verfuegbarkeit: verfuegbarkeitSchema,
    zimmer: num,
    badezimmer: num,
    separateWc: num,
    nasszellenTotal: num,
    etagen: num,
    etage: z.string(),
    raumhoehe: num,
    wohneinheiten: num,
    zustand: zustandSchema,
    baujahr: z.number().int(),
    letztesRenovationsjahr: z.number().int(),
    /**
     * Review 8-10 F5: HEDO «Sanierung / Jahr» as an ANSWER — «nein» = not
     * renovated since the build (IAZI renovationYear 0), «ja» = the year above.
     * Without it a never-renovated building could not answer the Pflicht field.
     */
    sanierung: jaNeinSchema,
    // ── B3-4 (16-9): what IAZI asks and the Checkliste lacked (analyse §7.5).
    //    Names = the Bewertung's (`paidMerkmale` / `objektMerkmale`), so the
    //    prefill «Bewertung aus Mandat» copies them without a bridge table.
    //    Baurecht itself is `eigentumsform: 'baurecht'`; only the expiry is new.
    haustyp: z.enum(HAUSTYPEN),
    /** Lage im Ort — IAZI qualityMicro 1 Ausserordentlich … 5 Schlecht, halve stappen. */
    lageImOrt: z.number().min(1).max(5).multipleOf(0.5),
    dachstockAusbaubar: jaNeinSchema,
    ferienobjekt: jaNeinSchema,
    luxusobjekt: z.enum(LUXUS_STUFEN),
    zaehlweiseZimmer: z.enum(ZAEHLWEISEN),
    servitute: jaNeinSchema,
    /** Only meaningful with eigentumsform «baurecht»: the expiry date (ISO). */
    baurechtVerfall: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    // ── Micro-6 (18-9): the four EWG fields of the IAZI Web Tool (Bewertung names).
    /** Zustand der Wohnung — the Checkliste's own condition words (→ IAZI flatCondition through the bridge). */
    wohnungszustand: zustandSchema,
    /** Lage im Gebäude — IAZI qualityFlatHouse 1 Sehr gut … 4 Schlecht, halve stappen (Gut = 2). */
    lageImGebaeude: z.number().min(1).max(4).multipleOf(0.5),
    /**
     * Wertquote in ‰ (IAZI ownershipPercent). 0 ‰ is no share (#587 MINOR-5) —
     * the CRM input stores a typed 0 as absent. NO ceiling here since PR 2: a
     * typed 1001 failed the autosave of the WHOLE Objekt section (master 23-9
     * 13:55); the draft keeps it with a hint, and the ceiling 1000 is the gate
     * of the prefill into a Bewertung (`paidMerkmaleSchema`).
     */
    wertquote: z.number().int().min(1),
    /** Gebäudetyp der Wohnung — Mehrfamilienhaus | Terrassenhaus (IAZI typeFlatBuildingCode). */
    gebaeudetyp: z.enum(GEBAEUDETYPEN),
    // ── IAZI-order 8-10 (B4): what the HEDO masks ask and the Checkliste lacked.
    /** EWG Wohnungstyp (IAZI typeFlatCode) — the Bewertung's keys. */
    wohnungstyp: z.enum(WOHNUNGSTYPEN),
    /** EWG «Mehrgeschossig» (IAZI maisonetteFlag). */
    maisonette: jaNeinSchema,
    /** Bauqualität — IAZI buildQuality 1 Sehr gut … 4 Schlecht, halve stappen. */
    bauqualitaet: z.number().min(1).max(4).multipleOf(0.5),
    // ── Order MFH-Rendite PR 2 (23-9): the Renditeliegenschaft's nine a1
    //    fields, under the Bewertung names (prefill 1:1), all in `objekt` —
    //    the CRM shows them as ONE group, only at an MFH (master 13:41).
    //    Two strictness levels, on purpose: the mix IS the paid schema (Σ ≥ 1,
    //    «unused» has one form: absent); the other eight are only as strict as
    //    the Checkliste's inputs guarantee — a schema error fails the autosave
    //    of the WHOLE section — and the paid bounds gate the prefill instead.
    /** Wohnungsspiegel — {@link wohnungsspiegelSchema}, the paid schema itself. */
    wohnungsspiegel: wohnungsspiegelSchema,
    /** Bürofläche HNF2 in m² (IAZI surfaceOffice). */
    bueroflaeche: num,
    /** Gewerbefläche HNF3 in m² (IAZI surfaceCommercial). */
    gewerbeflaeche: num,
    /** Verkaufs-/Lagerfläche HNF4 in m² (IAZI surfaceRetail). */
    verkaufsflaeche: num,
    /** Unterrichtsfläche HNF5 in m² (IAZI surfaceEducation). */
    unterrichtsflaeche: num,
    /** Medizinische Fläche HNF6 in m² (IAZI surfaceMedical). */
    medizinflaeche: num,
    /** Anzahl Aufzüge (IAZI liftNb) — a count; the input is whole. */
    aufzuege: z.number().int().nonnegative(),
    /**
     * Mietwert nachhaltig in CHF p.a., netto ohne Nebenkosten, inkl. Parkplätze (IAZI rentAnnual,
     * int32; IAZI 29-9: the effective rent is only information) — the input is whole.
     */
    jahresmiete: z.number().int().nonnegative(),
    /** IAZI-order 8-10 (B4): Mietertrag effektiv in CHF p.a. — information, never sent to IAZI. */
    mietertragEffektiv: z.number().int().nonnegative(),
    /** Mietanteil Geschäft in % (IAZI rentRepartitionPercent; one decimal is the prefill's gate). */
    mietanteilGeschaeft: num,
    highlights: z.string(),
    beschreibung: z.string(),
    eigentumsform: eigentumsformSchema,
    grundbuchNr: z.string(),
    neubauUmbau: z.boolean(),
})
    .partial();
export const flaechenSectionSchema = z
    .object({
    nettowohnflaeche: num,
    nutzflaeche: num,
    grundstuecksflaeche: num,
    balkonflaeche: num,
    terrasseGarten: num,
    gebaeudevolumenGva: num,
    /** B3-4: the norm the volume was measured in (IAZI volumeCode) — a number without its norm asserts nothing. */
    volumenNorm: z.enum(VOLUMEN_NORMEN),
    garagenparkplaetze: num,
    /** B3-4: Tiefgaragenplätze (IAZI parkOpenNb) — separate from Garagen/Abstellplätze. */
    tiefgaragenplaetze: num,
    aussenparkplaetze: num,
})
    .partial();
export const finanzenSectionSchema = z
    .object({
    wunschverkaufspreis: num,
    mindestverkaufspreis: num,
    verkaufspreisInserat: num,
    schaetzwertHedonisch: num,
    // B3-3 (16-9): the IAZI-Bewertung writes its result back here (product
    // endpoint POST /mandates/:id/bewertung) — band, date, qualityCode and the
    // pointer to the Bewertung. Read-only in the CRM; `schaetzwertHedonisch`
    // itself stays manually overridable. Additive: partial, no strict.
    schaetzwertHedonischVon: num,
    schaetzwertHedonischBis: num,
    schaetzwertHedonischDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    schaetzwertHedonischQualitaet: z.number().int().min(0).max(4),
    // B10 (RVB10 N1): WHICH interval Von/Bis carry — 50 (IAZI's 50 %, since B10)
    // or 95. Absent on write-backs from before B10 (their band was the 95 %).
    schaetzwertHedonischIntervall: bandIntervallSchema,
    schaetzwertHedonischBewertungId: z.string().uuid(),
    nebenkostenJahr: num,
    eigenmietwert: num,
    mwst: mwstSchema,
})
    .partial();
// GEAK ratings are OPTIONAL checklist add-ons (E1 exposé): staff enter them
// when a GEAK certificate exists; the exposé's energy page then renders the
// class ladder. Letter classes A–G (kept inline to avoid a cyclic import with
// schemas/expose.ts, which owns the exposé-facing GEAK schema).
const geakLetter = z.enum(['A', 'B', 'C', 'D', 'E', 'F', 'G']);
export const energieSectionSchema = z
    .object({
    waermeerzeugung: waermeerzeugungSchema,
    waermeverteilung: waermeverteilungSchema,
    heizkostenJahr: num,
    niedrigenergie: niedrigenergieSchema,
    geakKlasse: geakLetter,
    geakGebaeudehuelle: geakLetter,
    geakGesamtenergie: geakLetter,
    geakCo2: geakLetter,
    geakKwhProM2: num,
    geakCo2ProM2: num,
})
    .partial();
const bauteilBewertungSchema = z
    .object({ qualitaet: bauteilQualitaetSchema, zustand: bauteilZustandSchema })
    .partial();
export const zustandSectionSchema = z
    .object({
    // partialRecord: only the assessed components are sent; v4's z.record()
    // with an enum key would demand every Bauteil be present.
    bauteile: z.partialRecord(bauteilSchema, bauteilBewertungSchema),
    beschreibungFenster: z.string(),
    zustandsbeschreibung: z.string(),
})
    .partial();
export const ausstattungSectionSchema = z
    .object({
    ausstattung: z.array(ausstattungOptionSchema),
    annehmlichkeiten: z.array(annehmlichkeitOptionSchema),
    anschluesse: z.array(anschlussOptionSchema),
    bastelraum: jaNeinSchema,
    wichtigeStichworte: z.string(),
})
    .partial();
/** Umgebung is auto-derived (Google Places at Exposé time) — no manual input. */
export const umgebungSectionSchema = z.object({}).partial();
/** section-key → schema, so the backend can validate the right shape per PUT. */
export const checklistSectionSchemas = {
    mandat: mandatSectionSchema,
    objekt: objektSectionSchema,
    flaechen: flaechenSectionSchema,
    finanzen: finanzenSectionSchema,
    energie: energieSectionSchema,
    zustand: zustandSectionSchema,
    ausstattung: ausstattungSectionSchema,
    umgebung: umgebungSectionSchema,
};
/** Autosave upsert (debounced PUT per section). `data` validated against the
 *  section schema by the backend using `checklistSectionSchemas[section]`. */
export const checklistSectionUpsertSchema = z.object({
    section: checklistSectionSchema,
    data: z.record(z.string(), z.unknown()),
    completed: z.boolean().optional(),
    clientUpdatedAt: z.string().datetime().optional(),
});
// ───────────────────── Dokumentations-Import (Verkaufsdoku → Checkliste) ─────
//
// A makler uploads an EXISTING Verkaufsdokumentation (docKey `verkaufsdoku`,
// usually authored by ANOTHER brokerage) and the backend proposes checklist
// values from it. The result is a DRAFT that is shown for review and only
// written through the normal per-section PUT after the makler confirms —
// nothing here is persisted by the analyse call itself.
//
// THE HARD RULE, encoded in the types rather than in a comment: the per-section
// proposals carry NO contact fields. Third-party documentation carries the
// OTHER agency's contact details, so a name/phone/e-mail found in it is almost
// never the owner's. Everything person-shaped is quarantined in
// `imDokumentGefunden`, which is a SEPARATE branch of the response and has to
// be adopted field by field in the UI. `IMPORT_CONTACT_FIELD_KEYS` is what a
// structural test asserts against, so a future field added to the wrong branch
// fails a test instead of quietly reaching the checklist.
/** How sure the model is about one proposed value. */
export const IMPORT_KONFIDENZ = ['hoch', 'mittel', 'niedrig'];
export const importKonfidenzSchema = z.enum(IMPORT_KONFIDENZ);
/** Which party the contact block in the document belongs to. `makler` is the
 *  dangerous one: it is the SELLING AGENT of the source document, never the
 *  owner — the UI marks it red and refuses to pre-select it. */
export const IMPORT_KONTAKT_ROLLEN = ['makler', 'eigentuemer', 'unbekannt'];
export const importKontaktRolleSchema = z.enum(IMPORT_KONTAKT_ROLLEN);
/** Per-field provenance for one proposed value (`quelle` = page/section). */
export const importFeldMetaSchema = z.object({
    quelle: z.string().max(120).optional(),
    konfidenz: importKonfidenzSchema,
    /**
     * A short German note the review screen shows as a warning tag next to the
     * value: contact data was stripped out of a free-text field, or an amount
     * falls outside a plausible band. Deliberately a NOTE and not a rejection —
     * the makler decides; the screen's job is to make the doubt visible.
     */
    warnung: z.string().max(80).optional(),
    /**
     * Order IAZI 25-9 PR 2 (master 12:08, K3): `false` = offered but NEVER
     * pre-selected, even on an empty field — the value is an assumption the
     * makler must confirm (a Mietanteil of 0 % when the document names no
     * business rent), and `warnung` says why. Only `false` exists: the default
     * pre-selection rule is the review screen's, not the server's.
     */
    vorauswahl: z.literal(false).optional(),
});
/**
 * The checklist sections the import may propose values for. `mandat` is
 * ABSENT on purpose — it is the section that holds owner name / phone / e-mail
 * / Ansprechpartner, i.e. exactly the data that must never be auto-applied.
 * `umgebung` is absent because it has no manual fields at all.
 */
export const IMPORT_SECTIONS = [
    'objekt',
    'flaechen',
    'finanzen',
    'energie',
    'zustand',
    'ausstattung',
];
export const importSectionSchema = z.enum(IMPORT_SECTIONS);
/**
 * The `sektion` enum of the WIRE form — the six importable checklist sections
 * plus `objektAdresse`, the object's own address.
 *
 * The address is not a checklist section; it is a bucket the server routes to
 * the draft's separate `objektAdresse` branch, where the review screen offers
 * it as its own explicit adoption. Carrying it as a seventh `sektion` value
 * keeps the wire shape a single flat row list instead of adding a second
 * nested object — which is the whole point of the row form (see
 * `verkaufsdoku-extract.service.ts`: the API caps a strict schema at 16
 * union-typed parameters and the old nested shape had 82).
 *
 * `mandat` is still absent, so an owner/contact field remains structurally
 * unreachable from a section row.
 */
export const IMPORT_ZEILEN_SEKTIONEN = [...IMPORT_SECTIONS, 'objektAdresse'];
/** The address sub-fields a `sektion: 'objektAdresse'` row may carry. */
export const IMPORT_ADRESS_FELDER = ['strasse', 'plz', 'ort', 'kanton'];
/** The contact fields a quarantined `imDokumentGefunden` row may carry. */
export const IMPORT_KONTAKT_FELDER = [
    'eigentuemerName',
    'kontaktName',
    'kontaktFirma',
    'kontaktEmail',
    'kontaktTelefon',
];
/**
 * Contact-ish keys that may NEVER appear inside a section proposal. Used by the
 * backend to strip them defensively and by a structural test to prove the
 * response type cannot carry them.
 */
export const IMPORT_CONTACT_FIELD_KEYS = [
    'eigentuemerName',
    'eigentuemerHandy',
    'eigentuemerEmail',
    'eigentuemerAdresse',
    'ansprechpartnerUserId',
    'firma',
];
/** Contacts FOUND in the document — shown separately, never auto-applied. */
export const importKontaktFundSchema = z
    .object({
    eigentuemerName: z.string().max(200),
    kontaktName: z.string().max(200),
    kontaktEmail: z.string().max(200),
    kontaktTelefon: z.string().max(60),
    kontaktFirma: z.string().max(200),
    kontaktRolle: importKontaktRolleSchema,
})
    .partial();
/** Body of both import endpoints: optionally pin the source document. */
export const verkaufsdokuImportRequestSchema = z.object({
    documentId: z.string().uuid().optional(),
});
/** Body of the photo-apply endpoint: which candidates to persist. */
export const verkaufsdokuFotoUebernahmeSchema = z.object({
    documentId: z.string().uuid().optional(),
    /** Candidate ids (sha256 hex) — max mirrors MAX_FOTOS in the service. */
    ids: z
        .array(z.string().regex(/^[0-9a-f]{64}$/))
        .min(1)
        .max(40),
});
/**
 * Optional prefill for a NEW mandate's 'mandat' checklist section. A lenient
 * partial subset of the Mandat-section fields, so the "Neues Verkaufsmandat"
 * create-form (owner name + object address up front) or a lead can seed the
 * section before the wizard opens. Validated with the SAME `mandatSectionSchema`
 * (`.pick(...).partial()`) so the shape / leniency stays shared and single-sourced.
 */
export const mandateCreatePrefillSchema = mandatSectionSchema
    .pick({
    eigentuemerName: true,
    eigentuemerHandy: true,
    eigentuemerEmail: true,
    objektAdresse: true,
    eigentuemerAdresse: true,
    firma: true,
    datum: true,
})
    .partial()
    // Round-2 P4a: the contract-relevant fields captured on the "Neues
    // Verkaufsmandat" create-form so the Maklervertrag can be generated + signed
    // immediately — BEFORE the full checklist is filled. They live in OTHER
    // checklist sections, so the seed path (mandate.service.create) routes each to
    // its canonical section (objekt: Grundbuch-Nr + Eigentumsform; finanzen:
    // Wunschverkaufspreis) → `buildMaklervertragData` reads them straight after
    // create. Field shapes are single-sourced from the section schemas so the
    // dropdown options / leniency never drift. `richtpreis` is the create-form
    // name for `finanzen.wunschverkaufspreis` (the Maklervertrag Richtpreis).
    .extend({
    grundbuchNr: objektSectionSchema.shape.grundbuchNr,
    eigentumsform: objektSectionSchema.shape.eigentumsform,
    // E19-Nachzug: selbstbewohnt vs. Renditeobjekt entscheidet über den
    // Mieterspiegel-Block + die Investoren-Prosa im Exposé, ist also schon beim
    // Anlegen bekannt — und wird wie Grundbuch-Nr./Eigentumsform in die
    // `objekt`-Sektion geseedet (mandate.service.create).
    objektkategorie: objektSectionSchema.shape.objektkategorie,
    richtpreis: finanzenSectionSchema.shape.wunschverkaufspreis,
});
/** Create a mandate (internal, from the checklist Mandat step or manually). */
export const mandateCreateSchema = z.object({
    type: mandateTypeSchema.default('sale'),
    leadId: z.string().uuid().optional(),
    prefill: mandateCreatePrefillSchema.optional(),
});
/**
 * Staff PATCH of mandate-level toggles (round-2 feedback). Currently just the
 * Foto-Shooting on/off flag (`photoShootingPlanned`); a partial patch, so an
 * omitted field is left unchanged. Extend as more staff-editable mandate-level
 * fields land.
 */
export const mandateUpdateSchema = z
    .object({
    photoShootingPlanned: z.boolean(),
})
    .partial();
// ─────────────────────────── Maklervertrag (F4) ─────────────────────────────
// Deterministic template-merge — NO LLM (workflow doc §8.1): the Maklervertrag
// is a fixed template with variable fields, so the render is pure/testable and
// carries no hallucination risk. Facts verified against `Maklermandat Hans
// Tobler.pdf` (workflow doc §12).
/** Contractor (Auftragnehmer) is always Finavest AG; MLG is the market brand.
 *  v2 (2026-07): the body is now the VERBATIM client template
 *  ('Finavest AG_Maklervertrag Template.docx') — full legal text + Beilage 1 —
 *  replacing the earlier paraphrased 7-section summary. A material text change, so
 *  the version bumps; already-persisted snapshots keep their own frozen bodyText.
 *  v3 (2026-07, design pass): the legal wording is byte-identical to v2; the body
 *  gains the template's STRUCTURE — the 15 main clauses carry their visible
 *  "1."–"15." numbers (clause 5 is cross-referenced as "Ziffer 5", so the numbers
 *  are part of the text) and two-column rows (parties / clause-2 fields /
 *  signatures) use a literal TAB as the column separator, which the PDF engine
 *  lays out as real columns (a plain-text viewer just shows a tab). */
export const MAKLERVERTRAG_TEMPLATE_VERSION = 'v3';
/** Commission: 3% of the sale price + VAT, due on notarisation. */
export const MAKLERVERTRAG_HONORAR_PCT = 3;
export const FINAVEST_PARTY = {
    name: 'Finavest AG',
    address: 'Zikadenweg 27a, 3006 Bern',
    signer: 'Reto Grossen',
    brand: 'MLG Immobilien AG',
};
export const EIGENTUMSFORM_LABELS = {
    alleineigentum: 'Alleineigentum',
    miteigentum: 'Miteigentum',
    stockwerkeigentum: 'Stockwerkeigentum',
    baurecht: 'Baurecht',
};
/** ISO YYYY-MM-DD → de-CH DD.MM.YYYY. Leaves a non-ISO/empty value untouched so
 *  it is a pure, idempotent formatter (a legacy value passes through unchanged). */
export function formatSwissDate(value) {
    if (!value)
        return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
}
/** Format a checklist address blob as "Strasse, PLZ Ort" (skips empties). */
export function formatPipelineAddress(addr) {
    const a = (addr ?? {});
    const line1 = typeof a.strasse === 'string' ? a.strasse.trim() : '';
    const plz = typeof a.plz === 'string' ? a.plz.trim() : '';
    const ort = typeof a.ort === 'string' ? a.ort.trim() : '';
    const line2 = [plz, ort].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ');
}
/** Swiss thousands formatting: 3300000 → "3'300'000". */
export function formatChf(n) {
    return Math.round(n)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, "'");
}
/** Build the merge fields from the persisted checklist sections + fallbacks. */
export function buildMaklervertragData(sections, opts = {}) {
    const mandat = sections.mandat ?? {};
    const objekt = sections.objekt ?? {};
    const finanzen = sections.finanzen ?? {};
    const richtpreis = typeof finanzen.wunschverkaufspreis === 'number'
        ? finanzen.wunschverkaufspreis
        : typeof opts.richtpreisFallback === 'number'
            ? opts.richtpreisFallback
            : undefined;
    const eigentumsform = eigentumsformSchema.safeParse(objekt.eigentumsform);
    return {
        auftraggeberName: typeof mandat.eigentuemerName === 'string' ? mandat.eigentuemerName : '',
        auftraggeberAdresse: formatPipelineAddress(mandat.eigentuemerAdresse),
        auftraggeberEmail: typeof mandat.eigentuemerEmail === 'string' ? mandat.eigentuemerEmail : undefined,
        objektAdresse: formatPipelineAddress(mandat.objektAdresse),
        grundbuchNr: typeof objekt.grundbuchNr === 'string' ? objekt.grundbuchNr : undefined,
        eigentumsform: eigentumsform.success ? eigentumsform.data : undefined,
        richtpreis,
        honorarPct: MAKLERVERTRAG_HONORAR_PCT,
        // Emit the display-formatted date (DD.MM.YYYY) so the frozen body line and the
        // PDF footer never show the same date in two different formats.
        datum: formatSwissDate(opts.datum ?? (typeof mandat.datum === 'string' && mandat.datum ? mandat.datum : '')),
    };
}
/** The four Eigentumsform options in the template's fixed order, rendered as an
 *  explicit checkbox line ([X] = chosen). The Word form checkboxes are lost in the
 *  DOCX→text extraction, so we render them literally. No selection → all unchecked
 *  (the template's blank-form default). */
function eigentumsformCheckboxes(chosen) {
    return EIGENTUMSFORM.map((k) => `[${chosen === k ? 'X' : ' '}] ${EIGENTUMSFORM_LABELS[k]}`).join('  ');
}
/** Split a "Strasse, PLZ Ort" one-liner (as produced by {@link formatPipelineAddress})
 *  back into its two display lines for the Auftraggeber block; a value without the
 *  ", " separator stays on one line, an empty value yields no lines. */
function auftraggeberAddressLines(addr) {
    const s = (addr ?? '').trim();
    if (!s)
        return [];
    const i = s.indexOf(', ');
    return i === -1 ? [s] : [s.slice(0, i), s.slice(i + 2)];
}
/** Blank Unterschrift line — ONE column's rule (the template has one per party). */
const MAKLERVERTRAG_SIGNATURE_LINE = '______________________';
/** Pair a left/right column list into TAB-separated rows, padding the shorter
 *  side with '' (a row that is empty on the left thus STARTS with the tab). */
function tabRows(left, right) {
    return Array.from({ length: Math.max(left.length, right.length) }, (_, i) => `${left[i] ?? ''}\t${right[i] ?? ''}`);
}
/**
 * Render the Maklervertrag as plain text — the SES-signed snapshot. Pure &
 * deterministic (no LLM): this reproduces the client's template
 * ('Finavest AG_Maklervertrag Template.docx') VERBATIM — every legal clause
 * (Art. 412 ff OR, Vollmacht, 3%-Honorar, Notar-Vergütung, Exklusivität unter der
 * Marke MLG Immobilien AG, Nachweis-/Ausfall-Provision, 4-Monats-Laufzeit,
 * Datenschutz, Gerichtsstand Bern) plus the FULL Beilage 1 — with only the
 * party/object/Eigentumsform fields merged in. Owner decisions are baked into the
 * fixed text: NO "Pauschalzahlung", and the signature block names NO person (the
 * Auftragnehmer signs as "Finavest AG"). Because it is the byte-stable signed
 * snapshot, the whole contract is present verbatim — not a paraphrase.
 *
 * v3 structure (wording unchanged vs v2): the 15 main clauses are numbered
 * "1."–"15." like the template's autonumbering (the closing sentence and the
 * whole Beilage 1 stay unnumbered), and two-column rows carry a literal TAB as
 * column separator — parties (per-line paired, top-aligned), the clause-2 field
 * rows (label\tvalue) and the side-by-side signature block. NOTE: the Word
 * template itself mis-tabs "Finavest AG" into the LEFT signature column; that is
 * a template bug — semantically it belongs RIGHT, under Auftragnehmer, and that
 * is how we render it.
 */
export function renderMaklervertrag(d) {
    const auftraggeber = [
        d.auftraggeberName.trim(),
        ...auftraggeberAddressLines(d.auftraggeberAdresse),
    ].filter(Boolean);
    const objektAdresse = d.objektAdresse.trim();
    const grundbuchNr = d.grundbuchNr?.trim();
    const richtpreis = typeof d.richtpreis === 'number'
        ? `Richtpreis:\tCHF ${formatChf(d.richtpreis)}`
        : 'Richtpreis:\tCHF';
    const sig = MAKLERVERTRAG_SIGNATURE_LINE;
    return [
        'MAKLERVERTRAG',
        '',
        // ── Vertragsparteien (template table → tab-paired columns, top-aligned) ─────
        ...tabRows(['Auftraggeber', ...auftraggeber], ['Auftragnehmer', FINAVEST_PARTY.name, 'Zikadenweg 27a', '3006 Bern']),
        '',
        '1. Die Auftraggeber und der Auftragsnehmer in seiner Rolle als Vermittlungs- und/oder Nachweis-makler schliessen hiermit einen Maklervertrag i.S. von Art. 412 ff nach schweizerischem Obligationenrecht ab.',
        '',
        '2. Gegenstand dieses Maklervertrages ist folgende Liegenschaft:',
        `Liegenschaftsadresse:\t${objektAdresse}`,
        `Grundbuch-Nr.:\t${grundbuchNr ?? ''}`,
        richtpreis,
        `Eigentumsform:\t${eigentumsformCheckboxes(d.eigentumsform)}`,
        '',
        '3. Die Auftraggeber bestätigen hiermit, verfügungsberechtigte Eigentümer bzw. rechtsgültig bestellte Vertreter des Eigentümers zu sein, welche über die zu veräussernde Liegenschaft verfügen können.',
        '',
        '4. Der Auftragnehmer wird beauftragt und bevollmächtigt, alle für das Zustandekommen des Verkaufes erforderlichen Arbeiten auszuführen, wie Ausschreibung des Objektes, Erstellung einer Verkaufsdokumentation, Beschaffung von Grundbuchauszügen -und Akten von Behörden und Banken (welche sich auf das Vertragsobjekt beziehen), Organisation von- und Teilnahme an Besichtigungen vor Ort, Verhandlungen mit Interessenten, Behörden und Banken, Erstellen von Korrespondenzen, abschliessen einer Reservationsvereinbarung (nach Rücksprache mit der Verkäuferschaft) mit der Käuferschaft und einen Notar mit der Ausfertigung eines Kaufvertragsentwurfs zu beauftragen und je ein Exemplar desselben dem Käufer und den Auftraggebern zuzustellen.',
        '',
        '5. Das Maklerhonorar beträgt 3% vom Verkaufspreis (zuzüglich Mehrwertsteuer). Das Makler-honorar ist geschuldet, wenn der Grundstückkaufvertrag zustande gekommen ist. Mit der notariellen Beurkundung des Grundstückkaufvertrages ist das Maklerhonorar zur Zahlung fällig.',
        '',
        '6. Der verschreibende Notar ist berechtigt, das Maklerhonorar mit der Anzahlung der Käuferschaft, welche auf das Treuhandkonto des verschreibenden Notars einbezahlt wird, nach der Verschreibung direkt zu vergüten (in Anlehnung an Ziffer 5). Die Rechnung wird durch die Auftraggeber visiert.',
        '',
        '7. Die Vertragsparteien vereinbaren Exklusivität für den vorliegenden Maklervertrag, d.h. die Auftraggeber bestätigen, dass kein weiterer Maklervertrag für das vorgenannte Grundstück und Objekt besteht und verpflichten sich gleichzeitig, während der Vertragsdauer keinen weiteren Maklervertrag abzuschliessen, welcher die vorliegende Liegenschaft betrifft. Der Marktauftritt und die Vermarktung erfolgen unter der Marke MLG Immobilien AG.',
        '',
        '8. Weist der Auftragnehmer einen abschlusswilligen und zahlungsfähigen Kaufinteressenten nach, wollen aber die Auftraggeber den Verkauf mit diesem Interessenten aus irgendeinem Grund nicht mehr tätigen, so ist dennoch die gesamte Maklerprovision gemäss Ziffer 5 geschuldet.',
        '',
        '9. Wird das Kaufobjekt während der Laufzeit des vorliegenden Vertrages ohne jegliche Mitwirkung des Auftragnehmers durch die Auftraggeber verkauft, so hat der Makler gegenüber den Auftraggebern Anspruch auf den ganzen Maklerlohn gemäss Ziffer 5.',
        '',
        '10. Die Vertragsparteien vereinbaren eine Vertragsdauer von 4 Monaten ab Vertragsunter-zeichnung. Der Vertrag beginnt mit dem Unterschriftsdatum. Der Vertrag läuft jedoch stillschweigend für jeweils eine Dauer von zwei Monaten weiter, wenn nicht ein Monat vor Ablauf der Frist eine schriftliche Kündigung erfolgt. Er endet spätestens beim Verkauf des Objekts.',
        '',
        '11. Wird das Vertragsobjekt nach Ablauf der Vertragsdauer oder nach der Kündigung dieses Vertrages durch die Auftraggeber an einen durch den Auftragnehmer angeworbenen Interessenten verkauft oder ist ein sachlicher oder psychologischer Zusammenhang gegeben, so hat der Makler dennoch Anspruch auf das gesamte Maklerhonorar gemäss Ziffer 5.',
        '',
        '12. Allfällige Ausstiegskommissionen von Hypotheken, Gebühren und allfällige Grundstück-gewinnsteuern sind von der Verkäuferschaft bzw. den Auftraggebern selbst abzuklären und zu bezahlen.',
        '',
        '13. Die Auftraggeber verpflichten sich, dem Auftragnehmer umgehend alle erforderlichen Unterlagen gemäss Beilage 1, die zur Durchführung des Auftrages erforderlich sind, zu übergeben und ihm alle nötigen Informationen und Auskünfte zu erteilen und ihn bei seinen Verkaufsbemühungen in geeigneter Weise zu unterstützen.',
        '',
        '14. Der Auftraggeber erklärt sich damit einverstanden, dass der Auftragnehmer im Rahmen der Durchführung des Auftrages Daten erhebt, verarbeitet bzw. nutzt und diese, soweit für die Vertragserfüllung notwendig, potenziellen Käufern übermittelt. Die Auftraggeber verpflichten sich, alle Informationen, die ihm im Rahmen dieses Vertrages bekannt werden, vertraulich zu behandeln. Sie verpflichten sich insbesondere diese Informationen nicht an Dritte weiterzu-geben oder zugänglich zu machen.',
        '',
        '15. Als ausschliesslichen Gerichtsstand für Streitigkeiten, welche aus diesem Maklervertrag entstehen sollten, vereinbaren die Vertragsparteien Bern. Dieser Vertrag kann nur unter Einhaltung der Schriftform geändert oder ergänzt werden. Eine allfällige Rechtsunwirksamkeit einzelner oder mehrerer Bestimmungen dieses Vertrages hat nicht die Ungültigkeit des Vertrages als Ganzes zur Folge.',
        'Die Parteien erklären sich mit den in diesem Vertrag genannten Bestimmungen einverstanden und unterzeichnen diese Vereinbarung gegenseitig.',
        '',
        // ── Unterschriften (tab columns; NO person — Finavest AG signs RIGHT, under
        //    Auftragnehmer, fixing the template's own tab slip) ──────────────────────
        'Auftraggeber\tAuftragnehmer',
        '',
        `${sig}\t${sig}`,
        'Ort / Datum\tOrt / Datum',
        '',
        `${sig}\t${sig}`,
        `\t${FINAVEST_PARTY.name}`,
        'Vorname / Name in Blockschrift\tVorname / Name in Blockschrift',
        '',
        'BEILAGE 1',
        '',
        'Der Kunde verpflichtet sich, dem Makler die folgenden Unterlagen und Informationen zu übergeben. Die Liste ist nicht abschliessend.',
        '',
        'Unterlagen zur Liegenschaft',
        '•Aktueller Grundbuchauszug',
        '•Aktuelle amtliche Schätzung',
        '•Externe Liegenschaftsschätzung (falls vorhanden)',
        '•Kaufvertragsentwurf (falls vorhanden)',
        '•Katasterplan oder Situationsplan / Zonenplan',
        '•Gebäudeversicherungsausweis',
        '•Baupläne / kubische Berechnung / Baubeschrieb',
        '•Baurechtsvertrag + aktueller Baurechtszins (falls im Baurecht)',
        '•Verkaufsdokumentation oder Fotos des Objekts: Aussenaufnahmen /Innenaufnahmen',
        '•Dokumente zur Energieeinstufung (Energienachweis, GEAK, Minergie, etc.)',
        '•Zusätzliche Gebäudeinformationen (Heizungstyp, Verbrauch, Wohnfläche, Baujahr, etc.)',
        '•Mieterspiegel',
        '•Kostenzusammenstellung grössere Sanierung der letzten Jahre (Abrechnung, Belege)',
        '',
        'Zusätzliche Unterlagen für Neu- und Umbauprojekte',
        '•Baubewilligung / Rechtskraftsbescheinigung oder Baufreigabe',
        '•Bau- bzw. Umbaupläne & Baubeschrieb / kubische Berechnung',
        '•Kostenvoranschlag mit Baukostenplan-Nr.',
        '•Handwerkerverzeichnis',
        '•Bauversicherung / Bauzeitversicherung',
        '•Generalunternehmer-Vertrag / Totalunternehmer-Vertrag',
        '•Werkvertrag',
        '•Verkaufspreisliste bei EFH / STWE',
        '',
        'Zusätzliche Unterlagen bei Stockwerkeigentum',
        '•Kaufpreisliste + Dokumentation',
        '•Nutzungs- und Verwaltungsreglement der Stockwerkeigentümergemeinschaft',
        '•Stockwerkeigentumsbegründungs-Vertrag (mit Wertquotenaufteilung)',
        '•Bei Stockwerkeigentum im Baurecht: Nebenkostenabrechnungen inkl. Baurechtzins',
        '',
        'Zusätzliche Unterlagen bei Bauland',
        '•Baureglement / Zonenplan',
        '•Überbauung / Projekte (sofern vorhanden)',
        '•Auszug aus dem Altlastenverdachtskataster (auch für Gewerbeimmobilien)',
    ].join('\n');
}
/**
 * Simple Electronic Signature (SES) submission from the owner (workflow §8.2).
 *
 * `.trim()` STAAT VOOR `.min(3)`, en die volgorde is de hele regel. Zonder de
 * trim was `"   "` een geldige rechtsgeldige ondertekenaarsnaam: drie tekens,
 * `min(3)` tevreden, en de rij `mandate_contracts.signerName` hield daarna
 * spaties als bewijs van wie getekend heeft. Het portaal trimt zelf al voor het
 * verstuurt, maar dat is een SCHERM - een directe API-call ging er langs.
 *
 * `bodyTextHash` is de SHA-256 (64 hex) van de vertragstekst die de
 * ondertekenaar OP DAT MOMENT VOOR ZICH ZAG. Optioneel, en dat is bewust: de
 * maillink stuurt hem niet en blijft zich precies zo gedragen als voorheen.
 * Waar hij WEL meekomt, weigert de backend een handtekening onder een tekst die
 * intussen opnieuw is verstuurd (409 `contract_changed`) - zie
 * `ContractService.signAsOwner`.
 */
export const contractSignSchema = z.object({
    signerName: z.string().trim().min(3).max(200),
    consent: z.literal(true),
    bodyTextHash: z
        .string()
        .length(64)
        .regex(/^[0-9a-f]{64}$/)
        .optional(),
});
/**
 * Staff override of the auto-generated Maklervertrag body (G4, feedback #6b).
 * `null` (or a blank string) CLEARS the override → the body falls back to the
 * deterministic template-merge (auto-generate-from-checklist stays the default).
 */
export const contractOverrideSchema = z.object({
    bodyTextOverride: z.string().max(50000).nullable(),
});
/** How a Maklervertrag reached the `signed` state (mirrors the DB CHECK). */
export const CONTRACT_SIGNED_METHODS = ['electronic', 'written'];
export const contractSignedMethodSchema = z.enum(CONTRACT_SIGNED_METHODS);
/**
 * Staff "Schriftlich unterzeichnet" (VM3 Deel A): a Maklervertrag signed on PAPER
 * is processed with the SAME finalize core as the digital SES sign() — no e-sign
 * token. The scanned, signed PDF is uploaded as multipart alongside these OPTIONAL
 * fields; the file itself is validated + AV-scanned server-side. `signerName`
 * falls back to the owner name from the checklist when omitted; `signedDate` (ISO
 * YYYY-MM-DD) lets staff record the real paper-signature date, defaulting to now.
 */
export const contractMarkSignedManuallySchema = z.object({
    signerName: z.string().trim().min(1).max(200).optional(),
    signedDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        // The regex only checks the SHAPE — reject calendar-invalid dates (e.g.
        // 2026-02-31) that JS' Date would silently roll over to the next month,
        // stamping the wrong Unterschriftsdatum on the Maklervertrag. The regex above
        // guarantees the slices are numeric; round-trip the parsed Y/M/D and require
        // every component to survive unchanged.
        .refine((v) => {
        const y = Number(v.slice(0, 4));
        const mo = Number(v.slice(5, 7));
        const d = Number(v.slice(8, 10));
        const dt = new Date(Date.UTC(y, mo - 1, d));
        return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
    }, 'invalid_calendar_date')
        .optional(),
});
/**
 * Admin: change a portal customer account's login e-mail (VM3 Deel B). Rejected
 * when another account in the tenant already owns the address (`email_taken`) so
 * an admin can never hijack another customer's login. Mirrors the DTO email rule.
 */
export const portalChangeEmailSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
});
/**
 * The effective Maklervertrag body: a non-empty staff override wins, otherwise
 * the deterministic template-merge is rendered from the latest checklist `data`.
 * Pure — the single place that decides "manual vs auto", so the persisted signer
 * snapshot, the sign page and the CRM preview can never diverge.
 */
export function effectiveMaklervertragBody(override, data) {
    return override && override.trim() ? override : renderMaklervertrag(data);
}
// ─────────────────────────── Approval gate (F6) ─────────────────────────────
/**
 * Customer-proposed checklist correction (approval gate, workflow doc §6). The
 * customer never writes the canonical section directly; the proposal lands as
 * `pending` and staff apply it on approve. `data` is the FULL proposed section,
 * validated by the backend against `checklistSectionSchemas[section]`.
 */
export const mandateChangeProposeSchema = z.object({
    section: checklistSectionSchema,
    data: z.record(z.string(), z.unknown()),
    note: z.string().max(2000).optional(),
});
/**
 * Owner-decision editability scope (F6): a portal customer may only PROPOSE
 * corrections to the OBJEKT section (object facts + descriptions) and their OWN
 * contact fields in the Mandat section. Price/finance sections and every internal
 * field stay makelaar-only. The portal UI reads these to decide which correction
 * forms to render, and the backend `propose` gate enforces the SAME rule, so the
 * client restriction and the server enforcement can never drift.
 */
export const PROPOSABLE_SECTIONS = ['objekt', 'mandat'];
/** The Mandat-section keys a customer may propose — their own contact data only. */
export const MANDAT_PROPOSABLE_FIELDS = [
    'eigentuemerName',
    'eigentuemerHandy',
    'eigentuemerEmail',
    'eigentuemerAdresse',
];
/**
 * Enforce the proposable scope for a customer change-request. Pure — the single
 * source of truth for "may this customer propose this section / these fields":
 *  - only `PROPOSABLE_SECTIONS` may be proposed at all (else `section_not_proposable`);
 *  - the whole `objekt` section is proposable;
 *  - `mandat` is restricted to `MANDAT_PROPOSABLE_FIELDS` (else `field_not_proposable`).
 */
export function checkProposable(section, data) {
    if (!PROPOSABLE_SECTIONS.includes(section)) {
        return { ok: false, reason: 'section_not_proposable' };
    }
    if (section === 'mandat') {
        const allowed = new Set(MANDAT_PROPOSABLE_FIELDS);
        for (const key of Object.keys(data)) {
            if (!allowed.has(key))
                return { ok: false, reason: 'field_not_proposable' };
        }
    }
    return { ok: true };
}
// ─────────────────────────── Shooting (F7) ──────────────────────────────────
/** Staff proposes a Foto-Shooting date; the owner approves it in the portal. */
export const shootingProposeSchema = z.object({
    proposedAt: z.string().datetime(),
    durationMin: z.number().int().positive().max(600).optional(),
    location: z.string().max(300).optional(),
    notes: z.string().max(2000).optional(),
});
// ─────────────────────────── Agenda (G6) ────────────────────────────────────
/**
 * Agenda window query (G6): upcoming Foto-Shooting appointments for the CRM
 * Aufgaben tab. v1 is DB-sourced (MandateShooting only) — the Consultation +
 * CalendarEvent models are dormant and MS-Graph is a write-only stub, so the
 * agenda reads the DB, not Outlook. Outlook/consultation two-way sync is an
 * explicit follow-up.
 */
export const agendaQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(90).default(30),
});
// ─────────────────────────── Publication (F9) ───────────────────────────────
/** Publish the listing to one or more channels (workflow doc §Fase 6). */
export const publicationPublishSchema = z.object({
    channels: z.array(publicationChannelSchema).min(1),
});
// ───────────────────── Kundenportal-Verwaltung (PR-1) ───────────────────────
//
// Portal access became a property of the PERSON on 26-08-2026 (owner dossier
// §4): a `customer_accounts` row with a status, administered from the CRM
// through `/customers`, instead of a side effect of signing a Maklervertrag.
// These are the wire shapes of that surface. They live beside
// `portalChangeEmailSchema` — the one piece of the old mandate-bound admin
// block that survived the move unchanged — because the two are read together.
/**
 * WHOM to invite, named by the OBJECT the advisor is looking at rather than by
 * an e-mail address.
 *
 * Exactly one of the three, and that is a `superRefine` rather than a union so
 * the refusal is one machine-readable message (`exactly_one_target`) instead of
 * three parallel union errors a UI would have to guess between. The address is
 * deliberately NOT in this DTO: staff typing an address is how an invitation
 * ends up at the wrong person, and every one of the three objects already knows
 * the address it belongs to.
 */
export const kundenportalInviteSchema = z
    .object({
    leadId: z.string().uuid().optional(),
    mandateId: z.string().uuid().optional(),
    dossierId: z.string().uuid().optional(),
})
    .superRefine((v, ctx) => {
    const gesetzt = [v.leadId, v.mandateId, v.dossierId].filter((x) => x !== undefined).length;
    if (gesetzt !== 1) {
        ctx.addIssue({ code: 'custom', message: 'exactly_one_target', path: [] });
    }
});
/**
 * The two saved views of the account list, and the reason they are an enum and
 * not two booleans: "never activated" and "dormant for 90 days" are the two
 * questions the CRM asks about portal adoption, and a caller that could ask for
 * both at once would be asking for the empty set (`invited` and `active` are
 * disjoint).
 */
export const KUNDENPORTAL_FILTER = ['nie_aktiviert', 'inaktiv_90'];
export const kundenportalFilterSchema = z.enum(KUNDENPORTAL_FILTER);
/** How long "dormant" is, in days — shared so the label and the WHERE agree. */
export const KUNDENPORTAL_INAKTIV_TAGE = 90;
/** Tenant-wide account list: the two saved views plus a substring on the address. */
export const kundenportalListeQuerySchema = z.object({
    filter: kundenportalFilterSchema.optional(),
    /** Substring of the login address (citext → case-insensitive at the DB). */
    q: z.string().trim().min(1).max(254).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * The account's own audit slice. `limit` only — no cursor, no offset: this is a
 * "what happened lately" panel, and an operator who needs the full trail has
 * the audit read-API with its filters and its `purpose` requirement.
 */
export const kundenportalAktivitaetQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
